import { useMemo } from 'react';
import { TokenCategory, TokenState, TokenStatus } from '../stores/data/types';
import { filterTokenData, filterByTime, filterByLastUpdated } from '../utils/filterUtils';
import { useUIStore } from '../stores/ui/uiStore';
import { TokenStreamDeckFilter } from '../api/fe-management-system-api';
import { allCategories, allStatuses, useGlobalDeckFilterStore } from '../stores/ui/globalDeckFilterStore';

export const useDeckFilter = (
  data: TokenState[],
  apiFilter?: TokenStreamDeckFilter
) => {
  const { searchResultTokenAddresses, isSearchMode } = useUIStore();
  const { selectedCategories, selectedStatuses, retrospectiveTokens, isRetrospectiveTokenMode } = useGlobalDeckFilterStore();

  // 필터링된 데이터 계산 - API에서 받아온 필터를 직접 사용
  const filteredData = useMemo(() => {
    // 검색 모드일 때는 서버 검색 결과만 표시하며, 다른 모든 필터를 무시함
    if (isSearchMode && searchResultTokenAddresses) {
      return data.filter(token => 
        token.statistics && 
        searchResultTokenAddresses.includes(token.tokenAddress)
      );
    }

    let result = data.filter(item => item.statistics);

    // 아카이브된 항목 제외
    result = result.filter(item => !item.feTokenState?.isArchived);

    // API에서 받아온 덱별 필터 적용
    if (apiFilter) {
      result = filterTokenData(result, apiFilter as any);
      // 덱별 시간 필터 적용
      result = filterByTime(result, apiFilter.timeFilter, apiFilter.customTimeMinutes);
      // 덱별 Last Tx Time 시간 필터 적용
      const lastUpdatedTimeFilter = apiFilter.lastUpdatedTimeFilter || 'ALL';
      const lastUpdatedCustomTimeMinutes = apiFilter.lastUpdatedCustomTimeMinutes;
      result = filterByLastUpdated(result, lastUpdatedTimeFilter, lastUpdatedCustomTimeMinutes);
    }

    // 전역 덱 필터 (카테고리/상태) 적용
    const hasCategoryFilter = selectedCategories.length !== allCategories.length;
    const hasStatusFilter = selectedStatuses.length !== allStatuses.length;

    if (hasCategoryFilter || hasStatusFilter) {
      result = result.filter(token => {
        const category = token.feTokenState?.category || TokenCategory.NONE;
        const status = token.feTokenState?.status || TokenStatus.NONE;

        const categoryMatch = !hasCategoryFilter || selectedCategories.includes(category);
        const statusMatch = !hasStatusFilter || selectedStatuses.includes(status);

        return categoryMatch && statusMatch;
      });
    }

    if (isRetrospectiveTokenMode()) {
      result = result.filter(token => {
        return !!retrospectiveTokens[token.tokenAddress]
      });
    }

    return result;
  }, [data, searchResultTokenAddresses, isSearchMode, apiFilter, selectedCategories, selectedStatuses, isRetrospectiveTokenMode, retrospectiveTokens]);

  // 필터 아이콘 활성화 상태 확인
  const isFilterActive = apiFilter && (
    (apiFilter.customTimeMinutes && (apiFilter.customTimeMinutes.min !== undefined || apiFilter.customTimeMinutes.max !== undefined)) ||
    (apiFilter.lastUpdatedCustomTimeMinutes && (apiFilter.lastUpdatedCustomTimeMinutes.min !== undefined || apiFilter.lastUpdatedCustomTimeMinutes.max !== undefined)) ||
    Object.values(apiFilter).some(value =>
      value &&
      typeof value === 'object' &&
      'min' in value &&
      (value.min !== undefined || value.max !== undefined)
    )
  );

  return {
    filteredData,
    isFilterActive,
  };
};
